// src/pages/admin/Page_Dashboard/Page_Dashboard.js
//
// M06 + ext - Dashboard refactorizado completo:
// - Parte ALTA: 3 KPIs globales + 2 graficas.
// - Vertical K12: 3 KPIs + tabla salas + grafica rutas + analitica de uso.
// - Vertical REVI: 3 KPIs + grafica creadas vs usadas + top terapias + analitica.
// - Vertical Simulator: 3 KPIs + nota de instrumentacion pendiente.
// - Otros: 1 KPI + nota de detalle futuro.
// - Nav lateral sticky con anclas a las 5 secciones.
// - Toggle General/Plataforma en cabecera: solo afecta a la analitica de USO
//   de K12 y REVI (los KPIs estructurales se mantienen siempre reales).

import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, message, Button } from "antd";
import { Link } from "react-router-dom";
import {
  TeamOutlined,
  ApartmentOutlined,
  UserOutlined,
  ReadOutlined,
  BookOutlined,
  HeartOutlined,
  MedicineBoxOutlined,
  ClusterOutlined,
  CloudServerOutlined,
  QuestionCircleOutlined,
  ContactsOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../../hooks/useAuth";
import { dashboardApi } from "../../../api/dashboardAPI";
import { leadsApi } from "../../../api/leadsApi";
import { PageHeader, EducaThemeProvider } from "../../../ui";
import {
  KpiCard,
  ChartCard,
  SectionHeader,
  GlobalsLineChart,
  GlobalsDonut,
  VerticalSection,
  TopRoomsTable,
  MonthlyMiniLineChart,
  AnalyticsWidget,
  FunnelWidget,
  InfoPanel,
  DashboardSideNav,
  DashboardModeToggle,
  readStoredMode,
} from "../../../components/Admin/Dashboard/_shared";
import "./Page_Dashboard.scss";

export function Page_Dashboard() {
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [k12, setK12] = useState(null);
  const [revi, setRevi] = useState(null);
  const [simulator, setSimulator] = useState(null);
  const [otros, setOtros] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(() => readStoredMode());
  // F6 M4 - Stats de leads pendientes (suma new+contacted+qualified).
  const [pendingLeads, setPendingLeads] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        if (!accessToken)
          throw new Error("Token no disponible. Inicia sesion.");
        const [overviewData, k12Data, reviData, simulatorData, otrosData] =
          await Promise.all([
            dashboardApi.getOverview(accessToken),
            dashboardApi.getVerticalK12(accessToken, mode),
            dashboardApi.getVerticalRevi(accessToken, mode),
            dashboardApi.getVerticalSimulator(accessToken),
            dashboardApi.getVerticalOtros(accessToken),
          ]);
        if (alive) {
          setOverview(overviewData);
          setK12(k12Data);
          setRevi(reviData);
          setSimulator(simulatorData);
          setOtros(otrosData);
        }
      } catch (e) {
        const msg =
          e?.body?.message || e?.message || "Error cargando el dashboard";
        if (alive) {
          setError(msg);
          message.error(msg);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, mode]);

  // F6 M4 - Fetch ligero de leads stats (no bloquea el render del dashboard).
  // Solo necesitamos byStatus -> pageSize:1 ahorra payload.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!accessToken) return;
      try {
        const res = await leadsApi.list(accessToken, { pageSize: 1 });
        const by = res?.data?.byStatus || {};
        const pending =
          (by.new || 0) + (by.contacted || 0) + (by.qualified || 0);
        if (alive) setPendingLeads(pending);
      } catch {
        // Si falla, dejamos el contador en 0; no rompemos el dashboard.
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken]);

  const rolesDonut = overview?.rolesDonut || [];
  const yearsIndex = overview?.timeseries?.yearsIndex || [];
  const usersTimeseries = overview?.globals?.users?.timeseries || {};

  const globalKpis = useMemo(
    () => [
      {
        key: "partners",
        title: "Partners",
        value: overview?.totals?.partners ?? 0,
        icon: <TeamOutlined />,
        link: "/admin/dashboard/partners",
      },
      {
        key: "institutions",
        title: "Instituciones",
        value: overview?.totals?.institutions ?? 0,
        icon: <ApartmentOutlined />,
        link: "/admin/dashboard/institutions",
      },
      {
        key: "users",
        title: "Usuarios",
        subtitle: "Todos los roles",
        value: overview?.globals?.users?.total ?? 0,
        icon: <UserOutlined />,
        link: "/admin/users-v2",
      },
      // F6 M4 - Leads pendientes (new + contacted + qualified).
      {
        key: "pending-leads",
        title: "Leads pendientes",
        subtitle: "Sin cerrar",
        value: pendingLeads,
        icon: <ContactsOutlined />,
        link: "/admin/leads",
      },
    ],
    [overview, pendingLeads],
  );

  const k12Kpis = useMemo(
    () => [
      {
        key: "k12-institutions",
        title: "Instituciones K12",
        value: k12?.kpis?.institutions ?? 0,
        icon: <ApartmentOutlined />,
        link: "/admin/dashboard/institutions",
      },
      {
        key: "k12-teachers",
        title: "Profesores",
        value: k12?.kpis?.teachers ?? 0,
        icon: <ReadOutlined />,
        link: "/admin/dashboard/teachers",
      },
      {
        key: "k12-students",
        title: "Alumnos",
        value: k12?.kpis?.students ?? 0,
        icon: <BookOutlined />,
        link: "/admin/dashboard/students",
      },
    ],
    [k12],
  );

  const reviKpis = useMemo(
    () => [
      {
        key: "revi-institutions",
        title: "Instituciones REVI",
        value: revi?.kpis?.institutions ?? 0,
        icon: <ApartmentOutlined />,
      },
      {
        key: "revi-terapeutas",
        title: "Terapeutas",
        value: revi?.kpis?.terapeutas ?? 0,
        icon: <MedicineBoxOutlined />,
      },
      {
        key: "revi-pacientes",
        title: "Pacientes",
        value: revi?.kpis?.pacientes ?? 0,
        icon: <HeartOutlined />,
      },
    ],
    [revi],
  );

  const simulatorKpis = useMemo(
    () => [
      {
        key: "sim-institutions",
        title: "Instituciones Simulator",
        value: simulator?.kpis?.institutionsSimulator ?? 0,
        icon: <ApartmentOutlined />,
      },
      {
        key: "sim-active",
        title: "Simuladores activos",
        value: simulator?.kpis?.simuladoresActivos ?? 0,
        icon: <ClusterOutlined />,
      },
      {
        key: "sim-servers",
        title: "Servidores activos",
        value: simulator?.kpis?.serversActivos ?? 0,
        icon: <CloudServerOutlined />,
      },
    ],
    [simulator],
  );

  const therapiesSeries = useMemo(
    () => [
      { name: "Creadas", data: revi?.therapies?.created?.monthly || [] },
      { name: "Usadas", data: revi?.therapies?.used?.monthly || [] },
    ],
    [revi],
  );

  // Mapeo del shape de topTherapies al esperado por TopRoomsTable.
  const topTherapiesItems = useMemo(
    () =>
      (revi?.topTherapies || []).map((t) => ({
        id: t.therapyId,
        title: t.title,
        count: t.useCount,
      })),
    [revi],
  );

  // Aperturas REALES de sala (RoomOpenLog), separadas en uso con alumnos
  // (itinerario, evaluacion, tarea) y ojeo del catalogo por el profesor.
  const roomsUsedItems = useMemo(
    () =>
      (k12?.rooms?.used?.items || []).map((r) => ({
        id: r.sceneId || r.key,
        title: r.title,
        count: r.count,
      })),
    [k12],
  );

  const roomsViewedItems = useMemo(
    () =>
      (k12?.rooms?.viewed?.items || []).map((r) => ({
        id: r.sceneId || r.key,
        title: r.title,
        count: r.count,
      })),
    [k12],
  );

  const renderKpiCol = (k) => (
    <Col key={k.key} xs={24} sm={12} md={8}>
      <KpiCard
        title={k.title}
        value={k.value}
        subtitle={k.subtitle}
        icon={k.icon}
        loading={loading}
        actions={
          k.link ? (
            <Link to={k.link}>
              <Button type="link" size="small" style={{ padding: 0 }}>
                Ver listado
              </Button>
            </Link>
          ) : null
        }
      />
    </Col>
  );

  return (
    <EducaThemeProvider>
      <div className="page-dashboard">
        <PageHeader
          title="Dashboard"
          subtitle="Resumen global y por vertical de la plataforma"
          actions={<DashboardModeToggle value={mode} onChange={setMode} />}
        />

        {error ? (
          <div
            style={{
              padding: 12,
              background: "rgba(198,63,62,0.08)",
              color: "#c63f3e",
              borderRadius: 12,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        ) : null}

        <div className="dashboard-layout">
          <div className="dashboard-content">
            {/* SECCION 1 - DATOS GLOBALES */}
            <SectionHeader
              id="globales"
              title="Datos globales"
              subtitle="Vista de pajaro de toda la plataforma"
            />
            <Row gutter={[16, 16]}>{globalKpis.map(renderKpiCol)}</Row>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} md={14}>
                <ChartCard
                  title="Evolucion de usuarios"
                  subtitle="Total mensual, todos los roles"
                >
                  <GlobalsLineChart
                    timeseries={usersTimeseries}
                    yearsIndex={yearsIndex}
                    height={280}
                  />
                </ChartCard>
              </Col>
              <Col xs={24} md={10}>
                <ChartCard
                  title="Distribucion por roles"
                  subtitle="Total de usuarios agrupados por rol"
                >
                  <GlobalsDonut roles={rolesDonut} height={300} />
                </ChartCard>
              </Col>
            </Row>

            {/* SECCION 2 - VERTICAL K12 */}
            <VerticalSection
              id="k12"
              title="Educa360 K12"
              subtitle="Plataforma educativa para instituciones"
            >
              <Row gutter={[16, 16]}>{k12Kpis.map(renderKpiCol)}</Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <ChartCard
                    title="Salas mas usadas"
                    subtitle={`Aperturas con alumnos (itinerario, evaluacion, tarea) — ${k12?.rooms?.used?.total ?? 0} en total`}
                  >
                    <TopRoomsTable
                      items={roomsUsedItems}
                      metricLabel="aperturas"
                      emptyText="Aun no hay aperturas de sala en clase registradas"
                    />
                  </ChartCard>
                </Col>
                <Col xs={24} md={8}>
                  <ChartCard
                    title="Salas mas vistas"
                    subtitle={`El profesor abre la sala para ojearla — ${k12?.rooms?.viewed?.total ?? 0} en total`}
                  >
                    <TopRoomsTable
                      items={roomsViewedItems}
                      metricLabel="vistas"
                      emptyText="Aun no hay salas ojeadas desde el catalogo"
                    />
                  </ChartCard>
                </Col>
                <Col xs={24} md={8}>
                  <ChartCard
                    title="Rutas mas usadas"
                    subtitle="Top 10 rutas didacticas por adopcion entre docentes"
                  >
                    <TopRoomsTable
                      rooms={k12?.topRooms || []}
                      metricLabel="adopciones"
                      emptyText="Aun no hay rutas adoptadas por otros docentes"
                    />
                  </ChartCard>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <ChartCard
                    title="Rutas didacticas creadas"
                    subtitle={`Tendencia mensual ${k12?.learningPaths?.year ?? ""}`}
                  >
                    <MonthlyMiniLineChart
                      data={k12?.learningPaths?.monthly || []}
                      height={240}
                    />
                  </ChartCard>
                </Col>
              </Row>
              {k12?.usage ? (
                <AnalyticsWidget
                  title="Analitica de uso K12"
                  usage={k12.usage}
                />
              ) : null}
              {k12?.funnel ? (
                <FunnelWidget
                  title="Funnel publico K12 (pre-registro)"
                  funnel={k12.funnel}
                />
              ) : null}
            </VerticalSection>

            {/* SECCION 3 - VERTICAL REVI */}
            <VerticalSection
              id="revi"
              title="REVI Terapeutico"
              subtitle="Plataforma terapeutica VR"
            >
              <Row gutter={[16, 16]}>{reviKpis.map(renderKpiCol)}</Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <ChartCard
                    title="Terapias mas usadas"
                    subtitle="Top 10 por sesiones completadas"
                  >
                    <TopRoomsTable
                      items={topTherapiesItems}
                      metricLabel="sesiones"
                      emptyText="Aun no hay terapias con sesiones completadas"
                    />
                  </ChartCard>
                </Col>
                <Col xs={24} md={12}>
                  <ChartCard
                    title="Terapias creadas vs usadas"
                    subtitle="Tendencia mensual del anio actual"
                  >
                    <MonthlyMiniLineChart
                      series={therapiesSeries}
                      height={240}
                    />
                  </ChartCard>
                </Col>
              </Row>
              {revi?.usage ? (
                <AnalyticsWidget
                  title="Analitica de uso REVI"
                  usage={revi.usage}
                />
              ) : null}
            </VerticalSection>

            {/* SECCION 4 - VERTICAL SIMULATOR */}
            <VerticalSection
              id="simulator"
              title="EducaSimulator"
              subtitle="Centros de simulacion VR"
            >
              <Row gutter={[16, 16]}>{simulatorKpis.map(renderKpiCol)}</Row>
              <InfoPanel label="En instrumentacion">
                Las metricas de uso de Simulator (visitas, sesiones, tiempo de
                simulacion) estan pendientes de instrumentacion en un milestone
                posterior. De momento solo mostramos los KPIs estructurales.
              </InfoPanel>
            </VerticalSection>

            {/* SECCION 5 - OTROS */}
            <VerticalSection
              id="otros"
              title="Otros usuarios"
              subtitle="Roles que no encajan en K12, REVI ni Simulator"
            >
              <Row gutter={[16, 16]}>
                {renderKpiCol({
                  key: "otros-users",
                  title: "Usuarios sin vertical clasificada",
                  value: otros?.kpis?.usersOtros ?? 0,
                  icon: <QuestionCircleOutlined />,
                })}
                <Col xs={24} sm={12} md={16}>
                  <InfoPanel label="Detalle previsto en milestone posterior">
                    Aqui apareceran metricas de usuarios externos, demos,
                    cuentas de prueba (admin, user, client, free, educaverse) y
                    otras categorias que no encajan en los productos
                    principales. Pendiente de definicion de scope.
                  </InfoPanel>
                </Col>
              </Row>
            </VerticalSection>
          </div>

          <aside className="dashboard-side" aria-label="Navegacion lateral">
            <DashboardSideNav />
          </aside>
        </div>
      </div>
    </EducaThemeProvider>
  );
}
